import React, { useState } from "react";
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, IconButton, CircularProgress, Snackbar, Alert } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { fetchContacts, addContact } from "../../../redux/Slice/Contacts/contactsSlice.js"; // Adjust the path if necessary
import { parsePhoneNumberFromString, isValidPhoneNumber } from "libphonenumber-js";
import useContactChannel from "../../../features/whatsapp/hooks/useContactChannel";
import ContactChannelPicker from "./ContactChannelPicker";

const AddContact = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();

  // Local state for form fields
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");

  // Snackbar states
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const { loading } = useSelector((state) => state.contacts);
  // Meta vs a QR number (no choice shown unless the workspace has QR numbers) -- see useContactChannel.
  const channelState = useContactChannel(isOpen);

  const validateAndNormalizePhone = (raw) => {
    if (!raw || typeof raw !== "string") return { ok: false, message: "Phone is required" };
    // Remove spaces, hyphens, parentheses
    const cleaned = raw.replace(/[\s\-()]/g, "").trim();
    // Support inputs with or without leading '+'
    const candidate = cleaned.startsWith("+")
      ? cleaned
      : (cleaned.startsWith("00") ? "+" + cleaned.slice(2) : (cleaned.match(/^\d+$/) ? "+" + cleaned : cleaned));

    if (!isValidPhoneNumber(candidate)) {
      return { ok: false, message: "Enter a valid phone number in international format" };
    }
    const parsed = parsePhoneNumberFromString(candidate);
    if (!parsed || !parsed.isValid()) {
      return { ok: false, message: "Enter a valid phone number" };
    }
    // Store without '+' for backend requirement
    const digitsNoPlus = parsed.number.replace(/^\+/, "");
    return { ok: true, e164: parsed.number, digits: digitsNoPlus };
  };

  const handleSave = () => {
    const result = validateAndNormalizePhone(phone);
    if (!result.ok) {
      setPhoneError(result.message || "Invalid phone number");
      setSnackbar({ open: true, message: result.message || "Invalid phone number", severity: "error" });
      return;
    }

    const contactData = {
      phoneNumberID: result.digits, // store without '+' per backend requirement
      name,
      ...channelState.target, // {} for the Meta way; { platform: "qr", connectionId } for a QR number
    };

    dispatch(addContact(contactData))
      .unwrap()
      .then(() => {
        dispatch(fetchContacts({ page: 1, limit: 10, sortDirection: 'asc' }));
        setSnackbar({ open: true, message: `${name} created successfully!`, severity: "success" });
        onClose();
      })
      .catch((error) => {
        const rawMsg = typeof error === 'string' ? error : (error?.message || 'Failed to add contact');
        const msg = /already exists/i.test(rawMsg) ? 'Contact already exists' : rawMsg;
        setSnackbar({ open: true, message: msg, severity: "error" });
        onClose();
      });
  };

  // Clear the form when modal closes
  const handleCloseModal = () => {
    setName(""); // Reset name field
    setPhone(""); // Reset phone field
    onClose(); // Close the modal
  };

  return (
    <div>
      <Dialog
        open={isOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
            padding: 0,
            backgroundColor: (theme) => theme.palette.background.paper,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: "bold",
            fontSize: { xs: "16px", sm: "20px", md: "24px", lg: "24px", xl: "24px" },
            padding: { xs: "12px 16px", sm: "14px 18px", md: "16px 20px", lg: "16px 20px", xl: "16px 20px" },
            marginBottom: 0,
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          Create New Contact
          <IconButton
            onClick={handleCloseModal}
            size="small"
            aria-label="Close"
            sx={{
              color: (theme) => theme.palette.text.primary,
              "& .MuiSvgIcon-root": {
                fontSize: { xs: "20px", sm: "22px", md: "24px" },
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers={false}
          sx={{
            px: { xs: 2, sm: 2.5, md: 3, lg: 3, xl: 3 },
            pb: { xs: 2, sm: 2.5, md: 2.5, lg: 2.5, xl: 2.5 },
            flex: "0 1 auto",
            overflowY: "visible",
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 3, sm: 3.25, md: 3.5, lg: 3.5, xl: 3.5 },
              pt: { xs: 2.5, sm: 3, md: 3.25, lg: 3.25, xl: 3.25 },
              pb: { xs: 1, sm: 1.25, md: 1.5, lg: 1.5, xl: 1.5 },
            }}
          >
            <ContactChannelPicker channelState={channelState} />
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              size="medium"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  minHeight: { xs: 48, sm: 50, md: 52, lg: 52, xl: 52 },
                  "& fieldset": {
                    borderColor: (theme) => theme.palette.divider,
                  },
                  "&:hover fieldset": {
                    borderColor: (theme) => theme.palette.text.secondary,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: (theme) => theme.palette.primary.main,
                  },
                },
                "& .MuiInputLabel-root": {
                  fontSize: "1rem",
                  color: (theme) => theme.palette.text.secondary,
                },
                "& .MuiFormHelperText-root": { fontSize: "0.875rem" },
                "& .MuiOutlinedInput-input": {
                  fontSize: "1rem",
                  color: (theme) => theme.palette.text.primary,
                },
              }}
            />

            <TextField
              label="Phone Number"
              value={phone}
              onChange={(e) => {
                const val = e.target.value;
                setPhone(val);
                if (!val) {
                  setPhoneError("");
                  return;
                }
                const res = validateAndNormalizePhone(val);
                setPhoneError(res.ok ? "" : res.message);
              }}
              error={!!phoneError}
              helperText={phoneError || "Enter number in international format, e.g. +12025550123"}
              fullWidth
              required
              size="medium"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  minHeight: { xs: 48, sm: 50, md: 52, lg: 52, xl: 52 },
                  "& fieldset": {
                    borderColor: (theme) => theme.palette.divider,
                  },
                  "&:hover fieldset": {
                    borderColor: (theme) => theme.palette.text.secondary,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: (theme) => theme.palette.primary.main,
                  },
                },
                "& .MuiInputLabel-root": {
                  fontSize: "1rem",
                  color: (theme) => theme.palette.text.secondary,
                },
                "& .MuiOutlinedInput-input": {
                  fontSize: "1rem",
                  color: (theme) => theme.palette.text.primary,
                },
                "& .MuiFormHelperText-root": {
                  fontSize: "0.875rem",
                  color: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.78)"
                      : theme.palette.text.secondary,
                  "&.Mui-error": {
                    color: (theme) => theme.palette.error.main,
                  },
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            padding: { xs: "14px", sm: "16px 18px 18px 18px", md: "16px 20px 20px 20px", lg: "16px 20px 20px 20px", xl: "16px 20px 20px 20px" },
            justifyContent: "space-between",
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          <Button
            onClick={handleCloseModal}
            sx={{
              color: (theme) => theme.palette.text.secondary,
              textTransform: "none",
              fontSize: "16px",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || !!phoneError || !phone || !channelState.ready}
            sx={(theme) => ({
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              borderRadius: "24px",
              textTransform: "none",
              fontWeight: 600,
              padding: { xs: "6px 14px", sm: "8px 20px", md: "10px 26px" },
              fontSize: { xs: "12px", sm: "14px", md: "16px" },
              boxShadow: theme.palette.mode === "dark" ? "0 1px 0 rgba(255,255,255,0.06) inset" : "none",
              "&:hover": {
                bgcolor: theme.palette.primary.dark,
                color: theme.palette.primary.contrastText,
              },
              "&.Mui-disabled": {
                bgcolor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            })}
          >
            {loading ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%", maxWidth: 480, whiteSpace: "pre-line" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AddContact;
